import React, { useEffect } from 'react';
import Slick from 'react-slick';
import styled from 'styled-components';
import { layout } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock';
import 'slick-carousel/slick/slick.css';
import { Portal } from '../portal/Portal';
import { Icon } from '../icon/Icon';
import { Button } from '../button/Button';
import { Box } from '../grid/Grid';
import next from './next.svg';
import prev from './prev.svg';

const Mask = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${themeGet('zIndex.modalMask')};
  background-color: ${themeGet('colors.blacks.2')};
`;

const CloseButton = styled(Button).attrs({
  variant: 'link',
})`
  position: fixed;
  top: ${themeGet('lightbox.closeButton.top')};
  right: ${themeGet('lightbox.closeButton.right')};
  width: ${themeGet('lightbox.closeButton.size')};
  height: ${themeGet('lightbox.closeButton.size')};
  background-color: ${themeGet('lightbox.closeButton.backgroundColor')};
  border-radius: ${themeGet('lightbox.closeButton.borderRadius')};
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  cursor: pointer;
  z-index: ${themeGet('zIndex.fixed')};
`;

const StyledSlick = styled(Slick)`
  .slick-arrow {
    position: absolute;
    top: ${themeGet('lightbox.arrow.top')};
    bottom: ${themeGet('lightbox.arrow.bottom')};
    width: ${themeGet('lightbox.arrow.width')};
    height: ${themeGet('lightbox.arrow.height')};
    border: ${themeGet('lightbox.arrow.border')};
    border-radius: ${themeGet('lightbox.arrow.borderRadius')};
    background-color: ${themeGet('lightbox.arrow.backgroundColor')};
    z-index: 1;
    background-position: center;
    background-repeat: no-repeat;
    cursor: pointer;

    &.slick-prev {
      left: ${themeGet('lightbox.arrow.prev.left')};
      right: ${themeGet('lightbox.arrow.prev.right')};
      background-image: url(${prev});
    }

    &.slick-next {
      left: ${themeGet('lightbox.arrow.next.left')};
      right: ${themeGet('lightbox.arrow.next.right')};
      background-image: url(${next});
    }
  }
`;

const Slide = styled.div`
  display: inline-flex !important;
  justify-content: center;
  align-items: center;
  height: 100vh;
  outline: none;
`;

const SlideImage = styled.img.attrs(props => ({
  maxWidth: ['none', themeGet('lightbox.slideImage.desktopMaxWidth')(props)],
  maxHeight: ['none', themeGet('lightbox.slideImage.desktopMaxHeight')(props)],
}))`
  width: 100%;
  height: 100%;
  overflow: hidden;
  object-fit: contain;
  ${layout}
`;

const settings = {
  infinite: true,
  speed: 500,
  slidesToScroll: 1,
  slidesToShow: 1,
  arrows: true,
  prevArrow: <Box display={['none !important', 'inline-block !important']} />,
  nextArrow: <Box display={['none !important', 'inline-block !important']} />,
};

export const Lightbox = ({ images, currentIndex, show, onClose, ...props }) => {
  useEffect(() => {
    show ? disableBodyScroll() : clearAllBodyScrollLocks();
  }, [show]);

  return (
    show && (
      <Portal>
        <Mask>
          <CloseButton onClick={onClose}>
            <Icon name="close" fontSize={16} color="white" />
          </CloseButton>
          <StyledSlick initialSlide={currentIndex} {...settings} {...props}>
            {images.map((item, index) => (
              <Slide key={`slide-${index}`}>
                <SlideImage src={item.src} alt={item.alt} />
              </Slide>
            ))}
          </StyledSlick>
        </Mask>
      </Portal>
    )
  );
};
